import { useVuelidate } from '@vuelidate/core'
import { cloneDeep, unionBy } from 'lodash-es'
import pluralize from 'pluralize'
import {
  computed,
  isRef,
  nextTick,
  reactive,
  ref,
  toRaw,
  toRef,
  watch,
} from 'vue'

import { post } from '@/api'
import { OWNERSHIP_LIFECYCLE_GRID } from '@/constants'
import { useStore } from '@/store'
import { getModelIconClasses } from '@/utils'
import { escapeHtml } from '@/utils/sanitize'

import { useRecordClone } from './useRecordClone'
import { useRecordMeta } from './useRecordMeta'

/**
 * Composable for "new record" views.
 * Returns a full dv-compatible reactive object for use with content components
 * (RecordFieldSections, SampleView, AssayView) and NewItemShell.
 *
 * @param {Object} options
 * @param {string} options.appName
 * @param {string} options.modelName
 * @param {string} options.modelType
 * @param {string[]} [options.skipFieldNames] - Fields to skip from meta (e.g. ['id'])
 * @param {Object} [options.cloneData] - { id, keepAttributes, keepAnnotations, setParent }
 * @param {Object|Function} [options.initialRecord] - A complete record to use as the base
 *   instead of meta defaults.  Deep-cloned before use; any meta fields absent from it are
 *   back-filled with defaults.  Ideal for restoring a previously saved record (e.g. wizard
 *   navigate-back) or for loading a near-complete template.
 *   Takes priority over initialOverrides when both resolve to non-empty.
 * @param {Object|Function} [options.initialOverrides] - Partial field-level overrides applied
 *   on top of meta defaults.  Only the `.value` of each matching field is copied — fields
 *   not already present in the meta-default record are ignored.  Suited for pre-filling a
 *   small number of fields (e.g. parent links, cart items).
 * @param {string[]} [options.nonClonableFieldNames] - Additional fields to skip during cloning
 * @param {Function} [options.postCloneTransform] - (clonedData, record) => void, mutates clonedData
 */
export function useNewView({
  appName,
  modelName,
  modelType,
  skipFieldNames = [],
  cloneData = null,
  initialRecord = null,
  initialOverrides = null,
  nonClonableFieldNames = [],
  postCloneTransform = null,
}) {
  // Normalize params to refs so callers can pass plain values or refs
  appName = toRef(appName)
  modelName = toRef(modelName)
  modelType = toRef(modelType)
  const store = useStore()

  // --- Meta ---
  const metaResult = useRecordMeta({
    appName,
    modelName,
    modelType,
    skipFieldNames,
  })

  // --- Record ---
  const record = ref({})
  const isRecordReady = ref(false)

  function buildDefaultRecord(attrs) {
    const rec = {}
    for (const [name, field] of Object.entries(attrs)) {
      rec[name] = {
        value: 'default' in field ? field.default : '',
        name,
        category: field.category,
      }
    }
    return rec
  }

  function initRecord(attrs) {
    isRecordReady.value = false
    let rec = buildDefaultRecord(attrs)

    // initialRecord — use a complete record as the base (e.g. saved assay from
    // a wizard navigate-back, or a near-complete template). Deep-clone and
    // back-fill any meta fields it lacks.
    if (initialRecord) {
      const full =
        typeof initialRecord === 'function' ? initialRecord() : initialRecord
      if (full && Object.keys(full).length > 0) {
        rec = cloneDeep(toRaw(full))
        for (const [name, field] of Object.entries(buildDefaultRecord(attrs))) {
          if (!(name in rec)) rec[name] = field
        }
      }
    } else if (initialOverrides) {
      // initialOverrides — partial field-level overrides on top of defaults.
      // Only touches fields already present in the meta defaults.
      const raw = isRef(initialOverrides)
        ? initialOverrides.value
        : initialOverrides
      const overrides = typeof raw === 'function' ? raw() : raw
      if (overrides) {
        for (const [name, fieldData] of Object.entries(overrides)) {
          if (rec[name] && fieldData?.value !== undefined) {
            rec[name].value = fieldData.value
          }
        }
      }
    }

    record.value = rec
    isRecordReady.value = true
  }

  // Support initialOverrides as a ref — use undefined as "still loading" sentinel.
  // When a ref is passed, record init waits until the ref resolves (non-undefined).
  const initialOverridesRef = isRef(initialOverrides) ? initialOverrides : null

  function tryInitRecord(attrs) {
    if (!attrs || Object.keys(attrs).length === 0) return
    if (isRecordReady.value) return
    if (initialOverridesRef && initialOverridesRef.value === undefined) return
    initRecord(attrs)
  }

  // Initialize record from metadata defaults (once meta is ready)
  watch(metaResult.modelAttributes, tryInitRecord, { immediate: true })

  // If initialOverrides is a ref, also trigger init when it resolves
  if (initialOverridesRef) {
    watch(initialOverridesRef, () =>
      tryInitRecord(metaResult.modelAttributes.value),
    )
  }

  // --- Owned_by → permissions auto-update ---
  function updatePermissions(newOwnedBy) {
    if (!newOwnedBy) return
    const currentOwnedById = record.value?.owned_by?.value?.id
    const perms = cloneDeep(record.value.permissions?.value || {})

    // Remove previous owned_by from permissions
    if (currentOwnedById) {
      if (perms?.actions?.view?.groups) {
        perms.actions.view.groups = perms.actions.view.groups.filter(
          (g) => g?.id !== currentOwnedById,
        )
      }
      if (perms?.actions?.change?.groups) {
        perms.actions.change.groups = perms.actions.change.groups.filter(
          (g) => g?.id !== currentOwnedById,
        )
      }
    }

    // Add new owned_by to permissions
    const addedPerms = []
    const missView = !perms?.actions?.view?.groups?.find(
      (g) => g.id === newOwnedBy.id,
    )
    const missChange = !perms?.actions?.change?.groups?.find(
      (g) => g.id === newOwnedBy.id,
    )
    if (missView && perms?.actions?.view?.groups) {
      perms.actions.view.groups.push(newOwnedBy)
      addedPerms.push('view')
    }
    if (missChange && perms?.actions?.change?.groups) {
      perms.actions.change.groups.push(newOwnedBy)
      addedPerms.push('edit')
    }

    if (record.value.permissions) {
      record.value.permissions.value = perms
    }

    if (addedPerms.length > 0) {
      const addedPermsMsgString = addedPerms.join(' & ')
      store.commit('notify', {
        type: 'success',
        message: `${pluralize(
          'Permission',
          addedPerms.length,
        )} (${addedPermsMsgString}) added for ${newOwnedBy.name}`,
        duration: 5000,
      })
    }
  }

  // --- Edit ---
  function edit(field, value) {
    if (field.name in record.value) {
      record.value[field.name].value = value
    } else {
      record.value[field.name] = { name: field.name, value }
    }
    if (field.name === 'owned_by') {
      updatePermissions(value)
    }
  }

  function resetField(field) {
    const fieldMeta = metaResult.modelAttributes.value[field.name]
    const defaultValue = fieldMeta?.default
    edit(field, defaultValue)
  }

  function resetFields(fields) {
    fields.forEach((field) => resetField(field))
  }

  // --- Clone support ---
  const isLoadingClone = ref(false)
  const cloneId = cloneData?.id || ''
  const hasClone = cloneId && cloneData?.keepAttributes

  const cloneResult = hasClone
    ? useRecordClone({
        ...cloneData,
        appName,
        modelName,
        annotationTypes: metaResult.annotationTypes,
        nonClonableFieldNames,
      })
    : null

  const keptAnnotationKeys = computed(
    () => cloneResult?.keptAnnotationKeys.value ?? [],
  )

  const displayAnnotationNotification = computed(
    () => keptAnnotationKeys.value.length > 0,
  )

  const keptAnnotationsStr = computed(() =>
    keptAnnotationKeys.value
      .map((k) => record.value[k]?.name)
      .map(
        (name) => `<code class="has-text-primary">${escapeHtml(name)}</code>`,
      )
      .join(', '),
  )

  if (hasClone) {
    isLoadingClone.value = true

    // Merge clone data on top of default record once both are ready.
    // Non-clonable fields are already filtered out by useRecordClone.
    watch(
      [() => isRecordReady.value, cloneResult.clonedRecord],
      async ([ready, clonedData]) => {
        if (!ready || !clonedData || Object.keys(clonedData).length === 0)
          return

        const data = cloneDeep(clonedData)

        // Apply post-clone transform if provided
        if (postCloneTransform) {
          postCloneTransform(data, record.value)
        }

        for (const [name, fieldData] of Object.entries(data)) {
          if (name === 'permissions') {
            delete fieldData.value.allowed
            const defaultActions = record.value.permissions.value.actions

            for (const [action, defaults] of Object.entries(defaultActions)) {
              for (const subject of ['users', 'groups']) {
                fieldData.value.actions[action][subject] = unionBy(
                  fieldData.value.actions[action][subject],
                  defaults[subject],
                  'id',
                )
              }
            }
          }
          if (record.value[name]) {
            // Attribute field — overwrite value
            record.value[name].value = fieldData.value
          } else {
            // Annotation field — add it to the record
            record.value[name] = cloneDeep(fieldData)
          }
        }

        await nextTick()
        isLoadingClone.value = false
      },
      { immediate: true },
    )
  }

  // --- Loading state ---
  const isLoadingRecord = computed(() => isLoadingClone.value)

  // --- Validation ---
  const v$ = useVuelidate({}, {}, { $autoDirty: true })
  const hasValidationErrors = computed(() => v$.value.$invalid)

  // --- Save ---
  const isSaving = ref(false)
  async function createRecord() {
    const payload = { data: { results: record.value } }
    const url = `${appName.value}/${modelName.value}/?type=${modelType.value}`
    try {
      isSaving.value = true
      const response = await post(url, payload)
      return response.data.results
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to save record'
      store.commit('notify', { type: 'error', message: errorMessage })
      throw error
    } finally {
      isSaving.value = false
    }
  }

  // --- Reset ---
  function resetRecord() {
    const attrs = metaResult.modelAttributes.value
    if (attrs && Object.keys(attrs).length > 0) {
      initRecord(attrs)
    } else {
      record.value = {}
      isRecordReady.value = false
    }
  }

  // --- Field helpers (match useDetailView interface) ---
  function isReadonly(name) {
    return metaResult.modelAttributes.value[name]?.readonly === true
  }

  function fieldMeta(name) {
    return metaResult.modelAttributes.value[name]
  }

  function fieldData(nameOrField) {
    const name =
      typeof nameOrField === 'string' ? nameOrField : nameOrField?.name
    return record.value[name]
  }

  // --- Fields not relevant on new-record pages (server-assigned on save) ---
  const DETAIL_ONLY_FIELDS = new Set([
    'id',
    'stock_locations_breadcrumb',
    'created',
    'created_by',
    'modified',
    'modified_by',
    'is_frozen',
    'freeze_date',
    'deleted',
    'deleted_by',
    'deleted_date',
  ])

  const visibleFields = computed(() =>
    metaResult.visibleFields.value.filter(
      (f) => !DETAIL_ONLY_FIELDS.has(f.name),
    ),
  )

  // --- Ownership/lifecycle grid (filtered by new-page visibility) ---
  const ownershipLifecycleGrid = computed(() => {
    const visible = new Set(visibleFields.value.map((f) => f.name))
    return OWNERSHIP_LIFECYCLE_GRID.map((row) =>
      row.filter((n) => visible.has(n)),
    ).filter((row) => row.length > 0)
  })

  // --- Icon classes ---
  const modelIconClasses = computed(() => {
    const classes = getModelIconClasses(appName.value, modelName.value)
    return classes.includes('fa-question-circle') ? [] : classes
  })

  return reactive({
    // meta (from useRecordMeta)
    meta: metaResult.meta,
    isMetaReady: metaResult.isMetaReady,
    modelAttributes: metaResult.modelAttributes,
    viewAttributes: metaResult.viewAttributes,
    annotationTypes: metaResult.annotationTypes,
    defaultRecord: metaResult.defaultRecord,
    visibleFields,
    listedFields: metaResult.listedFields,
    orderedFields: metaResult.orderedFields,
    ownershipLifecycleGrid,
    customProperties: metaResult.customProperties,
    isAnnotatable: metaResult.isAnnotatable,
    isAttachmentable: metaResult.isAttachmentable,
    isOrderable: metaResult.isOrderable,
    isNoteable: metaResult.isNoteable,
    isShippable: metaResult.isShippable,

    // identity
    appName: computed(() => appName.value),
    modelName: computed(() => modelName.value),
    modelType: computed(() => modelType.value),

    // record
    record,
    editedRecord: record,
    isRecordReady,
    isRecordLoaded: isRecordReady,
    isLoadingRecord,
    recordName: '',

    // editing (always on for new records)
    isEditing: true,
    isDeleted: false,
    canEdit: true,
    edit,
    resetField,
    resetFields,

    // field helpers
    isReadonly,
    fieldMeta,
    fieldData,

    // validation
    v$,
    hasValidationErrors,

    // save
    isSaving,
    createRecord,

    resetRecord,

    // clone
    keptAnnotationKeys,
    displayAnnotationNotification,
    keptAnnotationsStr,

    // label
    modelLabel: computed(() => metaResult.meta.value?.model?.label || ''),

    // icon
    modelIconClasses,
  })
}
